import React from "react";
import type { FC, PropsWithChildren } from "react";
import PatientStyle from "../../styles/Patient.module.css";
import EnrollPatientStyle from "../../styles/EnrollPatient.module.css";
import InputField from "../../components/Modal/InputField";
import SelectInputBox from "../../components/Modal/SelectInputBox";
import { InputsSection } from "../../components/Modal";
import { inputPhoneMasking } from "../../utils/validators";
import { IState } from "../../hooks/patients/index.type";
import { IaddPatientModalStates } from "./AddPatientModal";
import { useSelector } from "react-redux";
import { loginSliceValue } from "../../redux/reducers/loginSlice";

const genderOptions = [
  { key: "male", value: "Male" },
  { key: "female", value: "Female" },
  { key: "other", value: "Other" },
];

export type AddPatientModalState = PropsWithChildren<{
  status: boolean;
}>;

type AddEditModalProps = PropsWithChildren<{
  addEditModalStates: IaddPatientModalStates;
  setAddEditModalStates: React.Dispatch<
    React.SetStateAction<IaddPatientModalStates>
  >;
  states?: IState[];
  stateLoading?: boolean;
  isUpdateMode?: boolean;
}>;

const PersonalInfoSection: FC<AddEditModalProps> = (props) => {
  const { userDetails } = useSelector(loginSliceValue);
  const loginUserRole = userDetails?.data?.user?.role;

  const disableContactFields =
    props.isUpdateMode && loginUserRole !== "rtmAdmin";

  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const zipValue = e.target.value.trim();

    if (zipValue.length > 5) {
      props.setAddEditModalStates((prev) => ({
        ...prev,
        zip: {
          ...prev.zip,
          value: zipValue,
          error: "ZIP code cannot be more than 5 digits",
        },
      }));
    } else {
      props.setAddEditModalStates((prev) => ({
        ...prev,
        zip: {
          ...prev.zip,
          value: zipValue,
          error: "",
        },
      }));
    }
  };

  return (
    <>
      <InputsSection customClass={PatientStyle.name_input_section}>
        <InputField
          label="Full Legal Name"
          value={props.addEditModalStates.name.value}
          onChange={(event) => {
            props.setAddEditModalStates((prevVal) => ({
              ...prevVal,
              name: { value: event.target.value, error: "" },
            }));
          }}
          errorMessage={props.addEditModalStates.name.error}
          style={{ maxWidth: "100%" }}
          customClass={EnrollPatientStyle.enrollInput}
          containerClass={EnrollPatientStyle.enrollInputContainer}
          labelClass={EnrollPatientStyle.enrollInputLabel}
        />
      </InputsSection>
      <InputsSection customClass={PatientStyle.form_grid_section}>
        <InputField
          label="Date of Birth"
          type="date"
          placeholder="MM/DD/YYYY"
          value={props.addEditModalStates.dobString.value}
          onChange={(event) =>
            props.setAddEditModalStates((prevVal) => ({
              ...prevVal,
              dobString: { value: event.target.value, error: "" },
            }))
          }
          errorMessage={props.addEditModalStates.dobString.error}
          max={new Date().toISOString().split("T")[0]}
          style={{ maxWidth: "100%" }}
          customClass={EnrollPatientStyle.enrollInput}
          containerClass={EnrollPatientStyle.enrollInputContainer}
          labelClass={EnrollPatientStyle.enrollInputLabel}
        />
        <SelectInputBox
          name="gender"
          label="Gender"
          value={props.addEditModalStates.gender.value}
          customClass={PatientStyle.custom_select_box}
          onChange={(event) => {
            props.setAddEditModalStates((prevVal) => ({
              ...prevVal,
              gender: { value: event.target.value, error: "" },
            }));
          }}
          errorMessage={props.addEditModalStates.gender.error}
          inputContainerStyle={{ maxWidth: "100%" }}
          containerClass={EnrollPatientStyle.enrollInput}
          labelClass={EnrollPatientStyle.enrollInputLabel}
        >
          <option
            value=""
            disabled
            hidden
            className={PatientStyle.custom_option}
          >
            Select Gender
          </option>
          {genderOptions.map((genderItem) => (
            <option key={genderItem.key} value={genderItem.key}>
              {genderItem.value}
            </option>
          ))}
        </SelectInputBox>
        <InputField
          label="Email"
          value={props.addEditModalStates.email.value}
          onChange={(event) => {
            props.setAddEditModalStates((prevVal) => ({
              ...prevVal,
              email: { value: event.target.value, error: "" },
            }));
          }}
          errorMessage={props.addEditModalStates.email.error}
          style={{ maxWidth: "100%" }}
          customClass={EnrollPatientStyle.enrollInput}
          containerClass={EnrollPatientStyle.enrollInputContainer}
          labelClass={EnrollPatientStyle.enrollInputLabel}
          disabled={disableContactFields}
        />
        <InputField
          label="Phone Number"
          value={props.addEditModalStates.phone.value}
          onChange={(event) => {
            props.setAddEditModalStates((prevVal) => ({
              ...prevVal,
              phone: {
                // Use the improved masking function
                value: inputPhoneMasking(event.target.value),
                error: "",
              },
            }));
          }}
          errorMessage={props.addEditModalStates.phone.error}
          style={{ maxWidth: "100%" }}
          customClass={EnrollPatientStyle.enrollInput}
          containerClass={EnrollPatientStyle.enrollInputContainer}
          labelClass={EnrollPatientStyle.enrollInputLabel}
          // disabled={props.isUpdateMode}
          disabled={disableContactFields}
        />
      </InputsSection>
      <InputsSection customClass={PatientStyle.address_section}>
        <InputField
          label="Address Line 1 (Optional)"
          value={props.addEditModalStates.address1.value}
          onChange={(event) => {
            props.setAddEditModalStates((prevVal) => ({
              ...prevVal,
              address1: { value: event.target.value, error: "" },
            }));
          }}
          errorMessage={props.addEditModalStates.address1.error}
          style={{ maxWidth: "100%" }}
          customClass={EnrollPatientStyle.enrollInput}
          containerClass={EnrollPatientStyle.enrollInputContainer}
          labelClass={EnrollPatientStyle.enrollInputLabel}
        />
        <InputField
          label="Address Line 2 (Optional)"
          value={props.addEditModalStates.address2.value}
          onChange={(event) => {
            props.setAddEditModalStates((prevVal) => ({
              ...prevVal,
              address2: { value: event.target.value, error: "" },
            }));
          }}
          errorMessage={props.addEditModalStates.address2.error}
          style={{ maxWidth: "100%" }}
          customClass={EnrollPatientStyle.enrollInput}
          containerClass={EnrollPatientStyle.enrollInputContainer}
          labelClass={EnrollPatientStyle.enrollInputLabel}
        />
      </InputsSection>
      <InputsSection customClass={PatientStyle.address_section_2}>
        <InputField
          label="City (Optional)"
          value={props.addEditModalStates.city.value}
          onChange={(event) => {
            props.setAddEditModalStates((prevVal) => ({
              ...prevVal,
              city: { value: event.target.value.trim(), error: "" },
            }));
          }}
          errorMessage={props.addEditModalStates.city.error}
          style={{ maxWidth: "100%" }}
          customClass={EnrollPatientStyle.enrollInput}
          containerClass={EnrollPatientStyle.enrollInputContainer}
          labelClass={EnrollPatientStyle.enrollInputLabel}
        />

        <SelectInputBox
          name="state"
          label="State (Optional)"
          value={props.addEditModalStates.state.value}
          customClass={PatientStyle.custom_select_box}
          onChange={(event) => {
            props.setAddEditModalStates((prevVal) => ({
              ...prevVal,
              state: { value: event.target.value, error: "" },
            }));
          }}
          errorMessage={props.addEditModalStates.state.error}
          inputContainerStyle={{ maxWidth: "100%" }}
          containerClass={EnrollPatientStyle.enrollInput}
          labelClass={EnrollPatientStyle.enrollInputLabel}
        >
          <option value="" disabled className={PatientStyle.custom_option}>
            Select State
          </option>
          {props.stateLoading ? (
            <option disabled>Loading states...</option>
          ) : (
            props.states?.map((state, index) => (
              <option key={index} value={state.abbreviation}>
                {state.name}
              </option>
            ))
          )}
        </SelectInputBox>

        <InputField
          label="Zip Code (Optional)"
          value={props.addEditModalStates.zip.value}
          // onChange={event => {
          //   props.setAddEditModalStates(prevVal => ({
          //     ...prevVal,
          //     zip: { value: event.target.value, error: '' },
          //   }));
          // }}
          onChange={handleZipChange}
          errorMessage={props.addEditModalStates.zip.error}
          style={{ maxWidth: "100%" }}
          customClass={EnrollPatientStyle.enrollInput}
          containerClass={EnrollPatientStyle.enrollInputContainer}
          labelClass={EnrollPatientStyle.enrollInputLabel}
        />
      </InputsSection>
    </>
  );
};

export default PersonalInfoSection;
